// =============================================================================
// SCHOOL CONFIGURATION - SINGLE SOURCE OF TRUTH
// =============================================================================
// This is the ONLY file that changes between school branches
// All other code references these constants for complete customization

// SCHOOL IDENTITY
export const SCHOOL_CONFIG = {
  // Basic Information
  id: 'test-school',
  name: 'SchoolLine',
  fullName: 'SchoolLine Student Management System',
  tagline: 'AI Powered Student Management System',
  
  // Contact Information
  contact: {
    phone: '+91-7736315005',
    email: 'info@schoolline.app',
    address: 'SchoolLine HQ, Tech City, State, PIN',
    website: 'https://schoolline.app',
  },
  
  // Branding & Theme
  branding: {
    // Logos and Assets
    logo: '/assets/app_icon.png',
    logoAlt: '/assets/logos/schoolline-logo-alt.png', // For dark backgrounds
    favicon: 'favicon.ico',
    
    // Base Color Palette (theme-independent)
    colors: {
      primary: '#e08a08',
      primaryLight: '#f5a623',
      primaryDark: '#b86d00',
      secondary: '#ea580c',
      accent: '#f59e0b',
      accentButton: '#fef3c7',
      accentDark: '#d97706',
      text: '#000000',
      // Additional gradient colors for seamless transitions
      gradientMid: '#f59e0b',
      gradientLight: '#fcd34d',
      gradientPale: '#fef3c7',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
      // Cancel button colors - following standard UX principles
      cancel: '#6b7280', // Neutral gray for cancel
      cancelHover: '#4b5563', // Darker gray on hover
      cancelActive: '#374151', // Even darker when active
      // Alternative: More traditional red-based cancel (if you prefer)
      // cancel: '#dc2626', // Red-600
      // cancelHover: '#b91c1c', // Red-700
      // cancelActive: '#991b1b', // Red-800
    },

    // Theme System Configuration
    themes: {
      // Neutral greys with the brand orange as the accent (same palette as the ERP).
      // Text colours are chosen for AA contrast on their backgrounds.
      light: {
        // Background Colors
        background: '#ffffff',
        backgroundSecondary: '#fafafa',
        backgroundTertiary: '#f5f5f5',
        surface: '#ffffff',
        surfaceElevated: '#ffffff',
        mutedBackground: '#fafafa',

        // Text Colors
        text: '#171717',
        textSecondary: '#525252',
        textTertiary: '#737373',
        textInverse: '#ffffff',
        link: '#8a4808',

        // Border Colors
        border: '#e5e5e5',
        borderLight: '#f0f0f0',
        borderMedium: '#d4d4d4',
        borderStrong: '#a3a3a3',

        // Component Specific
        cardBackground: '#ffffff',
        cardBorder: '#e5e5e5',
        navBackground: '#ffffff',
        navBorder: '#e5e5e5',
        sidebarBackground: '#ffffff',
        headerBackground: '#ffffff',

        // Dashboard Specific
        dashboardBackground: '#f5f5f5',
        statsCardBackground: '#ffffff',
        statsCardBorder: '#e5e5e5',

        // Interactive States
        hover: '#f5f5f5',
        active: '#fef8ee',
        focus: 'rgba(224, 138, 8, 0.2)',
        brandSoft: '#fef8ee',
        brandSoftText: '#8a4808',

        // Shadows
        shadowColor: 'rgba(0, 0, 0, 0.05)',
        shadowColorMedium: 'rgba(0, 0, 0, 0.08)',
        shadowColorStrong: 'rgba(0, 0, 0, 0.14)',

        // Input Fields
        inputBackground: '#ffffff',
        inputBorder: '#d4d4d4',
        inputFocus: '#e08a08',
        inputPlaceholder: '#a3a3a3',

        // Chat Bubbles
        chatUserBackground: '#fdecd2',
        chatUserText: '#171717',
        chatAssistantBackground: '#ffffff',
        chatAssistantText: '#171717',
        chatErrorBackground: '#fef2f2',
        chatErrorText: '#b91c1c',

        // Table Colors
        tableHeaderBackground: '#fafafa',
        tableHeaderText: '#525252',
        tableCellBackground: '#ffffff',
        tableCellText: '#262626',
        tableBorder: '#e5e5e5',
      },

      dark: {
        // Background Colors
        background: '#0f0f0f',
        backgroundSecondary: '#1f1f1f',
        backgroundTertiary: '#262626',
        surface: '#171717',
        surfaceElevated: '#262626',
        mutedBackground: '#1f1f1f',

        // Text Colors
        text: '#f5f5f5',
        textSecondary: '#d4d4d4',
        textTertiary: '#a3a3a3',
        textInverse: '#171717',
        link: '#f0af4b',

        // Border Colors
        border: '#2e2e2e',
        borderLight: '#262626',
        borderMedium: '#404040',
        borderStrong: '#525252',

        // Component Specific
        cardBackground: '#171717',
        cardBorder: '#2e2e2e',
        navBackground: '#171717',
        navBorder: '#2e2e2e',
        sidebarBackground: '#171717',
        headerBackground: '#171717',

        // Dashboard Specific
        dashboardBackground: '#0f0f0f',
        statsCardBackground: '#171717',
        statsCardBorder: '#2e2e2e',

        // Interactive States
        hover: '#262626',
        active: 'rgba(224, 138, 8, 0.16)',
        focus: 'rgba(240, 175, 75, 0.3)',
        brandSoft: 'rgba(224, 138, 8, 0.16)',
        brandSoftText: '#f5c77e',

        // Shadows
        shadowColor: 'rgba(0, 0, 0, 0.3)',
        shadowColorMedium: 'rgba(0, 0, 0, 0.4)',
        shadowColorStrong: 'rgba(0, 0, 0, 0.55)',

        // Input Fields
        inputBackground: '#1f1f1f',
        inputBorder: '#404040',
        inputFocus: '#f0af4b',
        inputPlaceholder: '#737373',

        // Chat Bubbles
        chatUserBackground: 'rgba(224, 138, 8, 0.16)',
        chatUserText: '#f5f5f5',
        chatAssistantBackground: '#171717',
        chatAssistantText: '#f5f5f5',
        chatErrorBackground: '#450a0a',
        chatErrorText: '#fca5a5',

        // Table Colors
        tableHeaderBackground: '#1f1f1f',
        tableHeaderText: '#a3a3a3',
        tableCellBackground: '#171717',
        tableCellText: '#e5e5e5',
        tableBorder: '#2e2e2e',
      },
    },

    // Theme Configuration
    themeConfig: {
      defaultTheme: 'light',
      allowThemeToggle: true,
      persistTheme: true,
      systemThemeDetection: true,
      transitionDuration: '200ms',
    },
    
    // Gradients (theme-aware)
    gradients: {
      primary: 'linear-gradient(135deg, #e08a08 0%, #b86d00 100%)',
      hero: 'linear-gradient(135deg, #e08a08 0%, #b86d00 100%)',
      card: {
        light: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
        dark: 'linear-gradient(145deg, #1e293b 0%, #334155 100%)',
      },
      accent: 'linear-gradient(135deg, #f5a623 0%, #f59e0b 100%)',
      subtle: {
        light: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
        dark: 'linear-gradient(135deg, #334155 0%, #475569 100%)',
      },
    },
    
    // Typography
    fonts: {
      primary: 'Inter, system-ui, sans-serif',
      secondary: 'Inter, system-ui, sans-serif',
      heading: 'Inter, system-ui, sans-serif',
      body: 'Inter, system-ui, sans-serif',
    },
    
    // Spacing and Layout
    borderRadius: '8px',
  },
  
  // Features Configuration
  features: {
    admissionForm: true,
    aiChat: true,
    notifications: true,
    hpcReport: true,
    timeTable: true,
    bulkReports: true,
    onlinePayment: true,
    studentPortal: true,
    parentPortal: true,
    teacherPortal: true,
    library: true,
    events: true,
    gallery: true,
    blog: true,
    testimonials: true,
    newsletter: true,
  },
  
  // External Links and Integrations
  externalLinks: {
    admissionForm: 'https://docs.google.com/forms/d/e/1FAIpQLSdPMnlpfVmFDE4HivIJCVEJkeaSUVNq04n93SSc_kwx1Jl2dA/viewform?embedded=true',
    socialMedia: {
      facebook: 'https://facebook.com/schoolline',
      instagram: 'https://instagram.com/schoolline',
      twitter: 'https://twitter.com/schoolline',
      linkedin: 'https://linkedin.com/company/schoolline',
      youtube: 'https://youtube.com/schoolline',
    },
    playStore: 'https://play.google.com/store/apps/details?id=com.schoolline.app',
    appStore: 'https://apps.apple.com/app/schoolline/id123456789',
  },
  
  // SEO and Meta Information
  seo: {
    title: 'SchoolLine - Student Management System',
    description: 'SchoolLine is a next-gen school management app powered by AI. From automated report cards to smart chat-based academic support, it streamlines teaching, learning, and school operations. Trusted by schools to deliver smarter education',
    keywords: 'schoolline, school management, education platform, technology, student portal, parent portal',
    ogImage: '/assets/og-images/schoolline-og.jpg',
    twitterCard: 'summary_large_image',
    canonicalUrl: 'https://schoolline.app',
  },
  
  // Content Configuration
  content: {
    // Hero Section
    hero: {
      title: 'Welcome to SchoolLine',
      subtitle: 'Modern School Management Made Simple',
      description: 'Experience the future of education management with our comprehensive platform designed for students, parents, and educators.',
      ctaText: 'Get Started',
      backgroundImage: '/assets/images/hero-bg.jpg',
    },
    
    // Login Section
    login: {
        title: 'School Dashboard',
        subtitle: 'Sign in to manage your school',
        description: 'Students, staff, attendance, fees and reports in one place. Ask Me answers questions about your school in plain words.',
        coverImage: '/assets/login_cover.jpg',
      },
    
    // About Section
    about: {
      title: 'About SchoolLine',
      description: 'SchoolLine is a comprehensive education management platform that connects students, parents, and educators in a seamless digital environment.',
      mission: 'To revolutionize education management through innovative technology solutions.',
      vision: 'Creating a connected educational ecosystem for the digital age.',
      values: ['Innovation', 'Excellence', 'Accessibility', 'Community'],
    },
    
    // Services/Features
    services: [
      {
        title: 'Student Management',
        description: 'Comprehensive student information system with attendance, grades, and progress tracking.',
        icon: 'student',
      },
      {
        title: 'Parent Portal',
        description: 'Keep parents engaged with real-time updates on their child\'s academic progress.',
        icon: 'parent',
      },
      {
        title: 'Teacher Tools',
        description: 'Powerful tools for educators to manage classes, assignments, and assessments.',
        icon: 'teacher',
      },
      {
        title: 'Communication Hub',
        description: 'Seamless communication between all stakeholders in the education process.',
        icon: 'communication',
      },
    ],
    
    // Footer
    footer: {
      copyright: `© ${new Date().getFullYear()} SchoolLine. All rights reserved.`,
      description: 'Empowering education through technology.',
      quickLinks: [
        { title: 'About Us', url: '/about' },
        { title: 'Contact', url: '/contact' },
        { title: 'Privacy Policy', url: '/privacy-policy' },
        { title: 'Terms of Service', url: '/terms' },
      ],
    },
  },
  
  // Technical Configuration
  technical: {
    // Firebase Configuration (if school-specific)
    firebase: {
      // Use default Firebase config or override here
      // projectId: 'schoolline-specific-project',
    },
    
    // API Configuration
    api: {
      baseUrl: process.env.REACT_APP_API_URL || 'https://api.schoolline.app',
      timeout: 30000,
    },
    
    // Analytics
    analytics: {
      googleAnalyticsId: 'GA_MEASUREMENT_ID',
      facebookPixelId: 'FB_PIXEL_ID',
    },
    
    // Performance
    performance: {
      enableServiceWorker: true,
      enableCodeSplitting: true,
      enableImageOptimization: true,
    },
  },
  
  // Deployment Configuration
  deployment: {
    domain: 'schoolline.app',
    subdomain: 'www',
    environment: process.env.NODE_ENV || 'production',
    version: '1.0.0',
  },
};

// =============================================================================
// THEME MANAGEMENT AND CSS VARIABLES
// =============================================================================

// Generate CSS Custom Properties for Dynamic Theming
export const generateCSSVariables = (theme = 'light') => {
  const themeColors = SCHOOL_CONFIG.branding.themes[theme];
  const baseColors = SCHOOL_CONFIG.branding.colors;
  
  return {
    // Base Brand Colors (theme-independent)
    '--app-primary': baseColors.primary,
    '--app-primary-light': baseColors.primaryLight,
    '--app-primary-dark': baseColors.primaryDark,
    '--app-secondary': baseColors.secondary,
    '--app-accent': baseColors.accent,
    '--app-accent-button': baseColors.accentButton,
    '--app-accent-dark': baseColors.accentDark || '#9333ea',
    '--app-success': baseColors.success,
    '--app-warning': baseColors.warning,
    '--app-error': baseColors.error,
    '--app-info': baseColors.info,
    // Add cancel colors
    '--app-cancel': baseColors.cancel,
    '--app-cancel-hover': baseColors.cancelHover,
    '--app-cancel-active': baseColors.cancelActive,
    
    // Theme-specific Colors
    '--app-background': themeColors.background,
    '--app-background-secondary': themeColors.backgroundSecondary,
    '--app-background-tertiary': themeColors.backgroundTertiary,
    '--app-surface': themeColors.surface,
    '--app-surface-elevated': themeColors.surfaceElevated,
    
    // Text Colors
    '--app-text': themeColors.text,
    '--app-text-secondary': themeColors.textSecondary,
    '--app-text-tertiary': themeColors.textTertiary,
    '--app-text-inverse': themeColors.textInverse,
    
    // Border Colors
    '--app-border': themeColors.border,
    '--app-border-light': themeColors.borderLight,
    '--app-border-medium': themeColors.borderMedium,
    '--app-border-strong': themeColors.borderStrong,
    
    // Component Specific
    '--app-card-background': themeColors.cardBackground,
    '--app-card-border': themeColors.cardBorder,
    '--app-nav-background': themeColors.navBackground,
    '--app-nav-border': themeColors.navBorder,
    '--app-sidebar-background': themeColors.sidebarBackground,
    '--app-header-background': themeColors.headerBackground,
    
    // Dashboard Specific
    '--app-dashboard-background': themeColors.dashboardBackground,
    '--app-stats-card-background': themeColors.statsCardBackground,
    '--app-stats-card-border': themeColors.statsCardBorder,
    
    // Interactive States
    '--app-hover': themeColors.hover,
    '--app-active': themeColors.active,
    '--app-focus': themeColors.focus,
    
    // Input Fields
    '--app-input-background': themeColors.inputBackground,
    '--app-input-border': themeColors.inputBorder,
    '--app-input-focus': themeColors.inputFocus,
    '--app-input-placeholder': themeColors.inputPlaceholder,
    
    // Chat Bubbles
    '--app-chat-user-background': themeColors.chatUserBackground,
    '--app-chat-user-text': themeColors.chatUserText,
    '--app-chat-assistant-background': themeColors.chatAssistantBackground,
    '--app-chat-assistant-text': themeColors.chatAssistantText,
    '--app-chat-error-background': themeColors.chatErrorBackground,
    '--app-chat-error-text': themeColors.chatErrorText,
    
    // Table Colors
    '--app-table-header-background': themeColors.tableHeaderBackground,
    '--app-table-header-text': themeColors.tableHeaderText,
    '--app-table-cell-background': themeColors.tableCellBackground,
    '--app-table-cell-text': themeColors.tableCellText,
    '--app-table-border': themeColors.tableBorder,
    
    // Shadows
    '--app-shadow-color': themeColors.shadowColor,
    '--app-shadow-color-medium': themeColors.shadowColorMedium,
    '--app-shadow-color-strong': themeColors.shadowColorStrong,
    
    // Layout
    '--app-border-radius': SCHOOL_CONFIG.branding.borderRadius,
    '--app-font-primary': SCHOOL_CONFIG.branding.fonts.primary,
    '--app-font-secondary': SCHOOL_CONFIG.branding.fonts.secondary,
    '--app-transition-duration': SCHOOL_CONFIG.branding.themeConfig.transitionDuration,
    
    // Dashboard UI tokens, used through the `ui-*` Tailwind colours (tailwind.config.js)
    '--ui-page': themeColors.dashboardBackground,
    '--ui-card': themeColors.cardBackground,
    '--ui-muted': themeColors.mutedBackground,
    '--ui-hover': themeColors.hover,
    '--ui-line': themeColors.border,
    '--ui-line-soft': themeColors.borderLight,
    '--ui-input': themeColors.inputBorder,
    '--ui-input-bg': themeColors.inputBackground,
    '--ui-ink': themeColors.text,
    '--ui-ink-2': themeColors.textSecondary,
    '--ui-ink-3': themeColors.textTertiary,
    '--ui-link': themeColors.link,
    '--ui-brand-soft': themeColors.brandSoft,
    '--ui-brand-soft-ink': themeColors.brandSoftText,

    // Enhanced Shadow System
    '--app-shadow-sm': `0 1px 2px 0 ${themeColors.shadowColor}`,
    '--app-shadow-md': `0 4px 6px -1px ${themeColors.shadowColorMedium}, 0 2px 4px -1px ${themeColors.shadowColor}`,
    '--app-shadow-lg': `0 10px 15px -3px ${themeColors.shadowColorMedium}, 0 4px 6px -2px ${themeColors.shadowColor}`,
    '--app-shadow-xl': `0 20px 25px -5px ${themeColors.shadowColorStrong}, 0 10px 10px -5px ${themeColors.shadowColorMedium}`,
    
    // Gradient System
    '--app-gradient-primary': `linear-gradient(135deg, ${baseColors.primary} 0%, ${baseColors.primaryDark} 100%)`,
    '--app-gradient-secondary': `linear-gradient(135deg, ${baseColors.secondary} 0%, ${baseColors.accent} 100%)`,
    '--app-gradient-success': `linear-gradient(135deg, ${baseColors.success} 0%, #059669 100%)`,
    '--app-gradient-warning': `linear-gradient(135deg, ${baseColors.warning} 0%, #D97706 100%)`,
    '--app-gradient-danger': `linear-gradient(135deg, ${baseColors.error} 0%, #DC2626 100%)`,
    '--app-gradient-info': `linear-gradient(135deg, ${baseColors.info} 0%, #2563EB 100%)`,
    '--app-gradient-card': SCHOOL_CONFIG.branding.gradients.card[theme] || SCHOOL_CONFIG.branding.gradients.card.light,
    '--app-gradient-subtle': SCHOOL_CONFIG.branding.gradients.subtle[theme] || SCHOOL_CONFIG.branding.gradients.subtle.light,
  };
};

// Default CSS Variables (light theme)
export const CSS_VARIABLES = generateCSSVariables('dark');

// =============================================================================
// THEME HELPER FUNCTIONS
// =============================================================================

// Theme Management
export const getThemeConfig = () => SCHOOL_CONFIG.branding.themeConfig;

export const getThemeColors = (theme = 'light') => {
  return SCHOOL_CONFIG.branding.themes[theme] || SCHOOL_CONFIG.branding.themes.light;
};

export const getBaseColors = () => SCHOOL_CONFIG.branding.colors;

export const getGradients = (theme = 'light') => {
  const gradients = SCHOOL_CONFIG.branding.gradients;
  return {
    ...gradients,
    card: gradients.card[theme] || gradients.card.light,
    subtle: gradients.subtle[theme] || gradients.subtle.light,
  };
};

// Apply theme to document root
export const applyTheme = (theme = 'light') => {
  if (typeof document === 'undefined') return; // SSR safety
  
  const root = document.documentElement;
  const variables = generateCSSVariables(theme);
  
  // Apply CSS variables to root
  Object.entries(variables).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
  
  // Add theme class to body for additional styling
  document.body.className = document.body.className.replace(/theme-\w+/g, '');
  document.body.classList.add(`theme-${theme}`);
  
  // Store theme preference if persistence is enabled
  if (SCHOOL_CONFIG.branding.themeConfig.persistTheme) {
    localStorage.setItem('schoolline-theme', theme);
  }
};

// Get stored theme preference
export const getStoredTheme = () => {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('schoolline-theme');
};

// Detect system theme preference
export const getSystemTheme = () => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// Get initial theme based on configuration
export const getInitialTheme = () => {
  const config = SCHOOL_CONFIG.branding.themeConfig;
  
  // Check stored preference first
  const storedTheme = getStoredTheme();
  if (storedTheme && ['light', 'dark'].includes(storedTheme)) {
    return storedTheme;
  }
  
  // Check system preference if enabled
  if (config.systemThemeDetection) {
    return getSystemTheme();
  }
  
  // Fall back to default
  return config.defaultTheme;
};

// Theme toggle function
export const toggleTheme = (currentTheme) => {
  const newTheme = currentTheme === 'light' ? 'dark' : 'light';
  applyTheme(newTheme);
  return newTheme;
};

// =============================================================================
// COMPUTED VALUES AND HELPERS
// =============================================================================

// Feature Flags
export const isFeatureEnabled = (featureName) => {
  return SCHOOL_CONFIG.features[featureName] || false;
};

// Contact Information Getters
export const getContactInfo = (type) => {
  return SCHOOL_CONFIG.contact[type] || '';
};

// Branding Getters (updated for theme system)
export const getBrandingValue = (property, theme = 'light') => {
  const keys = property.split('.');
  let value = SCHOOL_CONFIG.branding;
  
  // Handle theme-specific properties
  if (keys[0] === 'themes') {
    value = SCHOOL_CONFIG.branding.themes[theme] || SCHOOL_CONFIG.branding.themes.light;
    keys.shift(); // Remove 'themes' from the path
  }
  
  for (const key of keys) {
    value = value?.[key];
  }
  return value || '';
};

// External Links Getters
export const getExternalLink = (linkType) => {
  const keys = linkType.split('.');
  let value = SCHOOL_CONFIG.externalLinks;
  for (const key of keys) {
    value = value?.[key];
  }
  return value || '#';
};

// SEO Helpers
export const getSEOData = () => {
  // Check if we are on a student report page
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  
  if (pathname.startsWith('/student-report')) {
    return {
      title: 'Performance Report',
      description: 'Get Student Performance Report',
      keywords: SCHOOL_CONFIG.seo.keywords,
      ogImage: SCHOOL_CONFIG.seo.ogImage,
      canonicalUrl: SCHOOL_CONFIG.seo.canonicalUrl,
    };
  }

  return {
    title: SCHOOL_CONFIG.seo.title,
    description: SCHOOL_CONFIG.seo.description,
    keywords: SCHOOL_CONFIG.seo.keywords,
    ogImage: SCHOOL_CONFIG.seo.ogImage,
    canonicalUrl: SCHOOL_CONFIG.seo.canonicalUrl,
  };
};

// Content Helpers
export const getContent = (section) => {
  return SCHOOL_CONFIG.content[section] || {};
};

// =============================================================================
// THEME PROVIDER COMPONENT RECOMMENDATION
// =============================================================================
/*
  To implement the theme system in your React app, create a ThemeProvider component:

  ```jsx
  // src/contexts/ThemeContext.jsx
  import React, { createContext, useContext, useEffect, useState } from 'react';
  import { 
    getInitialTheme, 
    applyTheme, 
    toggleTheme as toggleThemeHelper,
    getThemeConfig 
  } from '../config/appConfig';

  const ThemeContext = createContext();

  export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
      throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
  };

  export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(getInitialTheme());
    const config = getThemeConfig();

    useEffect(() => {
      applyTheme(theme);
    }, [theme]);

    // Listen for system theme changes if enabled
    useEffect(() => {
      if (!config.systemThemeDetection) return;

      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        if (!localStorage.getItem('schoolline-theme')) {
          setTheme(e.matches ? 'dark' : 'light');
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }, [config.systemThemeDetection]);

    const toggleTheme = () => {
      setTheme(current => toggleThemeHelper(current));
    };

    return (
      <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, config }}>
        {children}
      </ThemeContext.Provider>
    );
  };
  ```

  Then wrap your app with the ThemeProvider and use the useTheme hook in components.
*/

// Default export for easy importing
export default SCHOOL_CONFIG; 